import {
  MessarioShortcode,
  MessarioTemplate,
  MessarioTemplatesPayload
} from '@lib/messarioTemplates/types';

export class MessarioTemplatesFetchFailure extends Error {
  constructor(
    public readonly kind: 'configuration' | 'network' | 'rejected' | 'invalid-response',
    message: string,
    public readonly status?: number
  ) {
    super(message);
    this.name = 'MessarioTemplatesFetchFailure';
  }
}

function panelOriginNormalized(panelOrigin: string) {
  return panelOrigin.replace(/\/+$/, '');
}

function isMessarioTemplate(item: unknown): item is MessarioTemplate {
  if(!item || typeof item !== 'object') {
    return false;
  }

  const row = item as MessarioTemplate;
  return typeof row.id === 'number' &&
    typeof row.key === 'string' &&
    typeof row.value === 'string';
}

function normalizeMessarioTemplate(item: unknown): MessarioTemplate | null {
  if(!isMessarioTemplate(item)) {
    return null;
  }

  const row = item as Record<string, unknown>;
  const sendAs = row.send_as === 'photo' || row.send_as === 'document' ?
    row.send_as :
    null;

  return {
    template_fingerprint: typeof row.template_fingerprint === 'string' ? row.template_fingerprint : '',
    id: item.id,
    key: item.key,
    value: item.value,
    has_media: row.has_media === true,
    send_as: sendAs,
    media_name: typeof row.media_name === 'string' ? row.media_name : null,
    media_url: typeof row.media_url === 'string' ? row.media_url : null
  };
}

function isMessarioShortcode(item: unknown): item is MessarioShortcode {
  if(!item || typeof item !== 'object') {
    return false;
  }

  const row = item as MessarioShortcode;
  return typeof row.id === 'number' &&
    typeof row.key === 'string' &&
    typeof row.label === 'string' &&
    (row.value === null || typeof row.value === 'string');
}

async function readJsonBody(
  response: Response
): Promise<Record<string, unknown>> {
  try {
    return await response.json() as Record<string, unknown>;
  } catch(err) {
    throw new MessarioTemplatesFetchFailure(
      'invalid-response',
      'Messario Panel returned an unreadable response.',
      response.status
    );
  }
}

export async function fetchMessarioTemplates(
  token: string,
  panelOrigin: string,
  fetchValue: typeof fetch = fetch
): Promise<MessarioTemplatesPayload> {
  if(!panelOrigin) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario templates access token is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/templates`,
      {
        method: 'GET',
        headers: {Authorization: `Bearer ${token}`}
      }
    );
  } catch(err) {
    throw new MessarioTemplatesFetchFailure(
      'network',
      'Could not load Messario message templates.'
    );
  }

  const body = await readJsonBody(response);

  if(!response.ok) {
    throw new MessarioTemplatesFetchFailure(
      'rejected',
      (typeof body.error === 'string' && body.error) || 'Messario rejected the templates request.',
      response.status
    );
  }

  if(!Array.isArray(body.templates)) {
    throw new MessarioTemplatesFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete templates response.',
      response.status
    );
  }

  return {
    templates: body.templates
      .map(normalizeMessarioTemplate)
      .filter((item): item is MessarioTemplate => item !== null),
    accountName: typeof body.account_name === 'string' ? body.account_name : ''
  };
}

/**
 * Fetch template media as a browser File (token already in media_url, same as offers logo).
 */
export async function fetchMessarioTemplateMediaFile(
  mediaUrl: string,
  mediaName?: string | null,
  fetchValue: typeof fetch = fetch
): Promise<File> {
  if(!mediaUrl) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario template media URL is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(mediaUrl, {method: 'GET'});
  } catch(err) {
    throw new MessarioTemplatesFetchFailure(
      'network',
      'Could not load Messario template media.'
    );
  }

  if(!response.ok) {
    throw new MessarioTemplatesFetchFailure(
      'rejected',
      'Messario rejected the template media request.',
      response.status
    );
  }

  const blob = await response.blob();
  const fileName = (typeof mediaName === 'string' && mediaName.trim()) ? mediaName.trim() : 'file';
  return new File([blob], fileName, {
    type: blob.type || 'application/octet-stream'
  });
}

export async function fetchMessarioShortcodes(
  token: string,
  panelOrigin: string,
  peerId: string | number,
  fetchValue: typeof fetch = fetch
): Promise<MessarioShortcode[]> {
  if(!panelOrigin) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario templates access token is missing.'
    );
  }

  const peer = String(peerId);
  if(!peer || !/^\d+$/.test(peer)) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario shortcodes peer_id is invalid.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/shortcodes?peer_id=${encodeURIComponent(peer)}`,
      {
        method: 'GET',
        headers: {Authorization: `Bearer ${token}`}
      }
    );
  } catch(err) {
    throw new MessarioTemplatesFetchFailure(
      'network',
      'Could not load Messario shortcodes.'
    );
  }

  const body = await readJsonBody(response);

  if(!response.ok) {
    throw new MessarioTemplatesFetchFailure(
      'rejected',
      (typeof body.error === 'string' && body.error) || 'Messario rejected the shortcodes request.',
      response.status
    );
  }

  if(!Array.isArray(body.shortcodes)) {
    throw new MessarioTemplatesFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete shortcodes response.',
      response.status
    );
  }

  return body.shortcodes.filter(isMessarioShortcode);
}

export async function putMessarioShortcodes(
  token: string,
  panelOrigin: string,
  peerId: string | number,
  values: Record<string, string | null>,
  fetchValue: typeof fetch = fetch
): Promise<MessarioShortcode[]> {
  if(!panelOrigin) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario templates access token is missing.'
    );
  }

  const peer = String(peerId);
  if(!peer || !/^\d+$/.test(peer)) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario shortcodes peer_id is invalid.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/shortcodes`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          peer_id: peer,
          values
        })
      }
    );
  } catch(err) {
    throw new MessarioTemplatesFetchFailure(
      'network',
      'Could not save Messario shortcodes.'
    );
  }

  const body = await readJsonBody(response);

  if(!response.ok) {
    throw new MessarioTemplatesFetchFailure(
      'rejected',
      (typeof body.error === 'string' && body.error) || 'Messario rejected the shortcodes save.',
      response.status
    );
  }

  if(!Array.isArray(body.shortcodes)) {
    throw new MessarioTemplatesFetchFailure(
      'invalid-response',
      'Messario Panel returned an incomplete shortcodes save response.',
      response.status
    );
  }

  return body.shortcodes.filter(isMessarioShortcode);
}

export type MessarioTemplateEffectsApplyPayload = {
  template_id: number,
  template_fingerprint: string,
  peer_id: string,
  sent_message_id: string,
  sent_text: string
};

/**
 * POST /api/telegram-web/ai/template-effects/apply after confirmed successful template send.
 */
export async function applyMessarioTemplateEffects(
  token: string,
  panelOrigin: string,
  payload: MessarioTemplateEffectsApplyPayload,
  fetchValue: typeof fetch = fetch
): Promise<void> {
  if(!panelOrigin) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario Panel origin is not configured.'
    );
  }

  if(!token) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario templates access token is missing.'
    );
  }

  const peer = String(payload.peer_id ?? '').trim();
  if(!peer || !/^\d+$/.test(peer)) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario template-effects peer_id is invalid.'
    );
  }

  const sentMessageId = String(payload.sent_message_id ?? '').trim();
  if(!sentMessageId) {
    throw new MessarioTemplatesFetchFailure(
      'configuration',
      'Messario template-effects sent_message_id is missing.'
    );
  }

  let response: Response;
  try {
    response = await fetchValue(
      `${panelOriginNormalized(panelOrigin)}/api/telegram-web/ai/template-effects/apply`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_id: Number(payload.template_id),
          template_fingerprint: payload.template_fingerprint,
          peer_id: peer,
          sent_message_id: sentMessageId,
          sent_text: String(payload.sent_text ?? '')
        })
      }
    );
  } catch{
    throw new MessarioTemplatesFetchFailure(
      'network',
      'Could not reach Messario Panel for template-effects apply.'
    );
  }

  if(!response.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = await readJsonBody(response);
    } catch{
      // ignore body parse for error message
    }
    throw new MessarioTemplatesFetchFailure(
      'rejected',
      (typeof body.message === 'string' && body.message) ||
        (typeof body.error === 'string' && body.error) ||
        'Messario rejected the template-effects apply request.',
      response.status
    );
  }
}
